import { useState } from "react";
import type { Account, SavingGoal } from "../../types";
import { money } from "../../lib/finance";
import { goalCurrentSaved } from "../../lib/goals";
import { resolveGoalCushionAccountId } from "../../lib/goal-reserves";
import { api } from "../../lib/api";
import FormActions from "../../components/FormActions";
import Modal from "../../components/Modal";

export interface GoalUnfloatModalProps {
  goals: SavingGoal[];
  accounts?: Account[];
  groupName: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function GoalUnfloatModal({
  goals,
  accounts = [],
  groupName,
  onClose,
  onSaved,
}: GoalUnfloatModalProps) {
  const currency = goals[0]?.currency;
  const totalPool = goals.reduce((sum, g) => sum + goalCurrentSaved(g), 0);

  // Default allocations: fund smaller ones first
  const [allocations, setAllocations] = useState<Record<string, string>>(() => {
    let poolLeft = totalPool;
    const sorted = [...goals].sort((a, b) => a.targetAmount - b.targetAmount);
    const initial: Record<string, string> = {};

    for (const g of sorted) {
      if (poolLeft >= g.targetAmount) {
        initial[g.id] = String(g.targetAmount);
        poolLeft -= g.targetAmount;
      } else {
        initial[g.id] = String(poolLeft);
        poolLeft = 0;
      }
    }
    return initial;
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const currentAllocated = Object.values(allocations).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0,
  );
  const diff = totalPool - currentAllocated;

  function applyPreset(preset: "fund_first" | "equal") {
    if (preset === "equal") {
      const share = Math.floor((totalPool / goals.length) * 100) / 100;
      const updated: Record<string, string> = {};
      goals.forEach((g, i) => {
        updated[g.id] = i === goals.length - 1 ? String(totalPool - share * (goals.length - 1)) : String(share);
      });
      setAllocations(updated);
    } else {
      let poolLeft = totalPool;
      const sorted = [...goals].sort((a, b) => a.targetAmount - b.targetAmount);
      const updated: Record<string, string> = {};
      for (const g of sorted) {
        if (poolLeft >= g.targetAmount) {
          updated[g.id] = String(g.targetAmount);
          poolLeft -= g.targetAmount;
        } else {
          updated[g.id] = String(poolLeft);
          poolLeft = 0;
        }
      }
      setAllocations(updated);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (Math.abs(diff) > 0.01) {
      setError(`Allocated total must equal the pool total (${money(totalPool, currency)}). Difference: ${money(diff, currency)}.`);
      return;
    }

    setSaving(true);
    setError("");

    const poolFallbackAccountId = goals
      .map((g) => resolveGoalCushionAccountId(g, accounts))
      .find((id): id is string => Boolean(id));

    try {
      await api("v2/goals/unfloat", {
        allocations: goals.map((g) => ({
          goalId: g.id,
          accountId: resolveGoalCushionAccountId(g, accounts) ?? poolFallbackAccountId,
          amount: allocations[g.id] || "0",
        })),
      });

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unfloat goals.");
      setSaving(false);
    }
  }

  return (
    <Modal title={`Separate goals: ${groupName}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="goal-form">
        {error && <div className="form-error" role="alert">{error}</div>}

        <p style={{ fontSize: "0.85rem", color: "var(--muted)", margin: 0 }}>
          You have <strong>{money(totalPool)}</strong> saved in the shared pool.
          Choose how to divide it among these independent goals:
        </p>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            className="small"
            onClick={() => applyPreset("fund_first")}
          >
            Fund smaller first
          </button>
          <button
            type="button"
            className="small"
            onClick={() => applyPreset("equal")}
          >
            Split equally
          </button>
        </div>

        <div className="goal-detail-list" style={{ marginTop: "0.25rem" }}>
          {goals.map((g) => (
            <div key={g.id} className="goal-detail-row">
              <div>
                <strong>{g.name}</strong>
                <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                  Target: {money(g.targetAmount, g.currency)}
                </div>
              </div>
              <input
                type="text"
                inputMode="decimal"
                style={{ width: "120px", textAlign: "right" }}
                value={allocations[g.id] ?? ""}
                onChange={(e) => setAllocations({ ...allocations, [g.id]: e.target.value })}
              />
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--line)" }}>
          <span>Total allocated: <strong>{money(currentAllocated, currency)}</strong></span>
          <span style={{ color: diff !== 0 ? "#d8c27a" : "var(--mint)" }}>
            {diff === 0 ? "Balanced" : `Remaining: ${money(diff, currency)}`}
          </span>
        </div>

        <FormActions label="Confirm split" busy={saving} disabled={Math.abs(diff) > 0.01} />
      </form>
    </Modal>
  );
}
